// PhysicalTherapyServices.js
import React from 'react';
import './physiotherapy-for-rehabilitation.css';
import { Link } from "react-router-dom";

function PhysicalTherapyServices() {
  const services = [
    {
      id: "post-surgical",
      title: "Post-Surgical Rehabilitation",
      description: "Specialized recovery plans for joint replacements, ligament repairs, and orthopedic surgeries.",
      features: ["Joint replacement recovery", "Ligament repair rehab", "Orthopedic post-op care"]
    },
    {
      id: "pain-management",
      title: "Pain Management",
      description: "Manual therapy and targeted exercises to alleviate chronic back, neck, and joint pain.",
      features: ["Manual therapy techniques", "Targeted exercises", "Chronic pain relief"]
    },
    {
      id: "neurological",
      title: "Neurological Rehabilitation",
      description: "Supporting patients with stroke recovery, Parkinson's, and other neurological conditions to improve balance and coordination.",
      features: ["Stroke recovery", "Parkinson's support", "Balance & coordination"]
    },
    {
      id: "geriatric",
      title: "Geriatric Care",
      description: "Focused programs to improve mobility, prevent falls, and maintain independence in older adults.",
      features: ["Fall prevention", "Mobility improvement", "Independence maintenance"]
    },
    {
      id: "sports",
      title: "Sports Injury Recovery",
      description: "Helping athletes of all levels return to peak performance safely and quickly.",
      features: ["Peak performance return", "Injury prevention", "Athlete-specific training"]
    }
  ];

  const whyChooseUs = [
    {
      id: "personalized",
      title: "Personalized Treatment Plans",
      description: "We don't believe in 'one-size-fits-all.' Every patient receives a comprehensive assessment and a plan tailored to their specific goals."
    },
    {
      id: "integrated",
      title: "Integrated Care",
      description: "As a multidisciplinary rehab center, our physical therapists work closely with our prosthetics and orthotics specialists to ensure seamless, holistic care."
    },
    {
      id: "facilities",
      title: "State-of-the-Art Facilities",
      description: "We use modern equipment and techniques to ensure your recovery is efficient and effective."
    },
    {
      id: "education",
      title: "Empowerment through Education",
      description: "We teach you the 'why' behind your exercises, giving you the tools to manage your health long after your sessions end."
    }
  ];

  const conditionsTreated = [
    "Joint Replacements", "Ligament Repairs", "Sports Injuries", "Chronic Back Pain",
    "Neck Pain", "Stroke Recovery", "Parkinson's Disease", "Arthritis", "Osteoporosis",
    "Balance Disorders", "Post-Fracture Recovery", "Workplace Injuries"
  ];

  return (
    <div className="pts-page">

      {/* Hero Section */}
      <section className="pts-hero-section">
        <div className="pts-hero-overlay">
          <div className="pts-hero-container">
            <div className="pts-hero-content">
              <span className="pts-hero-badge">Physical Therapy Services</span>
              <h1 className="pts-hero-title">
                Movement is <span className="pts-hero-highlight">Medicine</span>
              </h1>
              <p className="pts-hero-subtitle">
                Regain strength, improve mobility, and manage pain through evidence-based practices. 
                Whether recovering from surgery, managing a chronic condition, or rehabilitating a 
                sports injury, our expert therapists provide personalized care to get you back to your daily life.
              </p>
              <div className="pts-hero-buttons">
                <Link to="/contactus" className="pts-btn pts-btn-primary">Schedule Assessment</Link>
                <Link to="/contactus" className="pts-btn pts-btn-secondary">Book Appointment</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="pts-overview-section">
        <div className="pts-container">
          <div className="pts-overview-grid">
            <div className="pts-overview-content">
              <span className="pts-section-badge">Overview</span>
              <h2 className="pts-overview-heading">Your Journey to Recovery Starts Here</h2>
              <p className="pts-overview-text">
                At our center, we believe that movement is medicine. Our physical therapy department 
                is dedicated to helping you regain strength, improve mobility, and manage pain through 
                evidence-based practices.
              </p>
              <p className="pts-overview-text">
                Whether you are recovering from surgery, managing a chronic condition, or rehabilitating 
                a sports injury, our expert therapists provide personalized care to get you back to your 
                daily life.
              </p>
              <div className="pts-overview-stats">
                <div className="pts-overview-stat">
                  <span className="pts-overview-stat-number">1000+</span>
                  <span className="pts-overview-stat-label">Patients Treated</span>
                </div>
                <div className="pts-overview-stat">
                  <span className="pts-overview-stat-number">95%</span>
                  <span className="pts-overview-stat-label">Success Rate</span>
                </div>
                <div className="pts-overview-stat">
                  <span className="pts-overview-stat-number">20+</span>
                  <span className="pts-overview-stat-label">Years Combined Experience</span>
                </div>
              </div>
            </div>
            <div className="pts-overview-image-wrapper">
              <img src="/physiotherapy.png" alt="Physical Therapy" className="pts-overview-image" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Gallery Section */}
      <section className="pts-gallery-section">
        <div className="pts-container">
          <div className="pts-section-header">
            <span className="pts-section-badge">Our Services</span>
            <h2 className="pts-section-heading">What We Offer</h2>
            <p className="pts-section-subheading">Comprehensive physical therapy services for every need</p>
          </div>

          {/* <div className="pts-services-gallery">
            <img src="/therapy1.jpg" className="pts-gallery-img" alt="Physical therapy session" />
            <img src="/therapy2.jpg" className="pts-gallery-img" alt="Rehabilitation exercises" />
            <img src="/therapy3.jpg" className="pts-gallery-img" alt="Therapist helping patient" />
            <img src="/therapy4.jpg" className="pts-gallery-img" alt="Sports injury recovery" />
            <img src="/therapy5.jpg" className="pts-gallery-img" alt="Geriatric care" />
            <img src="/therapy6.jpg" className="pts-gallery-img" alt="Neurological rehabilitation" />
          </div> */}

          <div className="pts-services-grid">
            {services.map((service) => (
              <div key={service.id} className="pts-service-card">
                <h3 className="pts-service-title">{service.title}</h3>
                <p className="pts-service-description">{service.description}</p>
                <ul className="pts-service-features">
                  {service.features.map((feature, index) => (
                    <li key={index} className="pts-service-feature">
                      <span className="pts-service-feature-dot"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="pts-why-section">
        <div className="pts-container">
          <div className="pts-section-header">
            <span className="pts-section-badge pts-section-badge-light">Our Approach</span>
            <h2 className="pts-section-heading pts-section-heading-light">Why Choose Us?</h2>
            <p className="pts-section-subheading pts-section-subheading-light">Excellence in every aspect of your care</p>
          </div>
          <div className="pts-why-grid">
            {whyChooseUs.map((item) => (
              <div key={item.id} className="pts-why-card">
                <h3 className="pts-why-title">{item.title}</h3>
                <p className="pts-why-description">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions Treated Section */}
      <section className="pts-conditions-section">
        <div className="pts-container">
          <div className="pts-conditions-grid">
            <div className="pts-conditions-image-wrapper">
              <img src="/physiotherapy2.jpg" alt="Conditions We Treat" className="pts-conditions-image" />
            </div>
            <div className="pts-conditions-content">
              <span className="pts-section-badge">Conditions</span>
              <h2 className="pts-conditions-heading">Conditions We Treat</h2>
              <p className="pts-conditions-text">
                Our expert physical therapists are experienced in treating a wide range of conditions, 
                from acute injuries to chronic conditions. We work closely with you and your physician 
                to develop the optimal treatment plan.
              </p>
              <div className="pts-conditions-tags">
                {conditionsTreated.map((condition, index) => (
                  <span key={index} className="pts-conditions-tag">{condition}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pts-cta-section">
        <div className="pts-container">
          <div className="pts-cta-content">
            <h2 className="pts-cta-heading">Ready to Start Your Recovery?</h2>
            <p className="pts-cta-subheading">
              Don't let pain or limited mobility hold you back. Schedule your assessment today and 
              take the first step toward a stronger, more active you.
            </p>
            <div className="pts-cta-buttons">
              <Link to="/contactus" className="pts-btn pts-btn-primary">Contact Us</Link>
              <Link to="/contactus" className="pts-btn pts-btn-secondary-light">Book an Appointment</Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

export default PhysicalTherapyServices;